/**
 * intentRouter.ts — AI Agent Intent Dispatcher
 *
 * Architecture:
 *   User types instruction in RightAgentPanel
 *     → routeInstruction() is called
 *       → calls /api/agent-dispatch (Doubao LLM with tool_calling)
 *         → LLM returns a tool_call JSON
 *           → dispatchToolCall() maps tool name → store action
 *
 * Fallback: if the API call fails, fallbackDispatch() handles
 * common patterns with simple regex, keeping the UI responsive.
 */

import { useContentStore } from "@/store/useContentStore";
import { markdownToHtml } from "@/lib/markdownToHtml";

/* ─── Tool definitions (OpenAI-compatible function calling) ───── */

export const AGENT_TOOLS = [
  {
    type: "function",
    function: {
      name: "update_outline",
      description:
        "Rewrite or update the article outline in the Content Outline section (Step 1).",
      parameters: {
        type: "object",
        properties: {
          text: {
            type: "string",
            description: "The full new outline in Markdown format.",
          },
        },
        required: ["text"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "update_article",
      description:
        "Rewrite or update the full article body in the Writing Canvas (Step 2).",
      parameters: {
        type: "object",
        properties: {
          html: {
            type: "string",
            description: "The full article content in HTML or Markdown.",
          },
        },
        required: ["html"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "update_banner_copy",
      description:
        "Write or update the Facebook / LinkedIn banner ad copy in Social Media Snippets (Step 3).",
      parameters: {
        type: "object",
        properties: {
          text: {
            type: "string",
            description:
              "The banner/ad copy text, including hashtags.",
          },
        },
        required: ["text"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "update_video_script",
      description:
        "Write or update the TikTok / Reels video hook and script in Social Media Snippets (Step 3).",
      parameters: {
        type: "object",
        properties: {
          text: {
            type: "string",
            description:
              "The video hook (0-3 sec) + main talking points + CTA.",
          },
        },
        required: ["text"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "set_title",
      description:
        "Set or replace the selected article title shown in the top control bar (Part 1).",
      parameters: {
        type: "object",
        properties: {
          title: {
            type: "string",
            description: "The new article title string.",
          },
        },
        required: ["title"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "add_keyword",
      description: "Add a new keyword to the Keywords list in Setup (Part 1).",
      parameters: {
        type: "object",
        properties: {
          keyword: {
            type: "string",
            description: "The keyword string to add.",
          },
        },
        required: ["keyword"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "reply",
      description:
        "Send a plain conversational reply when no workflow state needs to change.",
      parameters: {
        type: "object",
        properties: {
          message: {
            type: "string",
            description: "The reply text to show in the chat.",
          },
        },
        required: ["message"],
      },
    },
  },
] as const;

/* ─── Tool name union ─────────────────────────────────────────── */

export type ToolName =
  | "update_outline"
  | "update_article"
  | "update_banner_copy"
  | "update_video_script"
  | "set_title"
  | "add_keyword"
  | "reply";

export interface ResolvedToolCall {
  name: ToolName;
  args: Record<string, string>;
}

/* ─── Dispatcher: tool call → store action ────────────────────── */

/**
 * Given a resolved tool call from the LLM, write its payload
 * directly into the Zustand store and return a human-readable
 * confirmation message to display in the agent chat.
 */
export function dispatchToolCall(toolCall: ResolvedToolCall): string {
  // Access store imperatively (safe outside React render)
  const store = useContentStore.getState();

  switch (toolCall.name) {
    case "update_outline":
      store.updateOutline(toolCall.args.text ?? "");
      store.setOutlineGenerated(true);
      return "✅ **Outline updated.** The Content Outline section has been rewritten — review it above and adjust if needed.";

    case "update_article": {
      // LLM returns Markdown despite the field being named "html" —
      // convert it so the contentEditable editor receives real HTML.
      const raw = toolCall.args.html ?? toolCall.args.text ?? "";
      store.updateArticle(markdownToHtml(raw));
      // Automatically dismiss any compliance warnings — the rewrite just fixed them.
      store.setViolations([]);
      return "✅ **Article updated** and compliance violations cleared. The Writing Canvas now contains the revised, OJK-safe version.";
    }

    case "update_banner_copy":
      store.updateBannerCopy(toolCall.args.text ?? "");
      return "✅ **Banner copy written** to Step 3 → Banner / Ad Copy.";

    case "update_video_script":
      store.updateVideoScript(toolCall.args.text ?? "");
      return "✅ **Video script written** to Step 3 → Video Script / Hook.";

    case "set_title":
      store.setSelectedTitle(toolCall.args.title ?? "");
      return `✅ **Title updated** to: "${toolCall.args.title}". It is now shown in the top control bar.`;

    case "add_keyword":
      store.addKeyword(toolCall.args.keyword ?? "");
      return `✅ Added keyword **"${toolCall.args.keyword}"** to the keyword list.`;

    case "reply":
      return toolCall.args.message ?? "Understood.";

    default:
      return "Done.";
  }
}

/* ─── Context snapshot passed to the LLM ─────────────────────── */

export interface InstructionContext {
  stage: string;
  keywords: string[];
  outline: string;
  articleHtml: string;
  /** Current banner / ad copy in Step 3 (empty string if not yet generated) */
  bannerCopy?: string;
  /** Current video script in Step 3 (empty string if not yet generated) */
  videoScriptCopy?: string;
}

/* ─── Main router: instruction → LLM → dispatch ──────────────── */

/**
 * Core intent routing function.
 *
 * 1. Builds a system prompt that includes current workflow context.
 * 2. Calls /api/agent-dispatch with the instruction + tool definitions.
 * 3. LLM responds with a tool_call JSON.
 * 4. dispatchToolCall() writes the result into the Zustand store.
 * 5. Returns the confirmation message string (for chat display).
 *
 * If the API call fails for any reason, fallbackDispatch() handles
 * common patterns locally so the UI never hangs.
 */
export async function routeInstruction(
  userInstruction: string,
  context: InstructionContext,
  modelOverride?: string
): Promise<string> {
  const systemPrompt = buildSystemPrompt(context);

  try {
    const response = await fetch("/api/agent-dispatch", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        messages: [
          { role: "system", content: systemPrompt },
          { role: "user", content: userInstruction },
        ],
        tools: AGENT_TOOLS,
        tool_choice: "auto",
        ...(modelOverride ? { modelOverride } : {}),
      }),
    });

    if (!response.ok) {
      throw new Error(`agent-dispatch returned ${response.status}`);
    }

    const data = (await response.json()) as {
      toolCall?: ResolvedToolCall;
      message?: string;
    };

    if (data.toolCall) {
      return dispatchToolCall(data.toolCall);
    }

    // LLM chose plain text (no tool call)
    return data.message ?? "Understood.";
  } catch (err) {
    console.warn("[intentRouter] API failed, using local fallback:", err);
    return fallbackDispatch(userInstruction);
  }
}

/* ─── System prompt builder ───────────────────────────────────── */

function buildSystemPrompt(ctx: InstructionContext): string {
  const bannerLen = ctx.bannerCopy?.length ?? 0;
  const videoLen  = ctx.videoScriptCopy?.length ?? 0;

  return `You are an AI Editorial Agent embedded in an SEO content creation tool.
You have full write access to the user's workflow through the provided tools.

Current workflow state:
- Stage: ${ctx.stage}
- Keywords: ${ctx.keywords.join(", ") || "(none yet)"}
- Outline length: ${ctx.outline.length} chars
- Article length: ${ctx.articleHtml.length} chars
- Banner copy length: ${bannerLen} chars ${bannerLen ? "(already written — improve it)" : "(not yet generated)"}
- Video script length: ${videoLen} chars ${videoLen ? "(already written — improve it)" : "(not yet generated)"}

Tool selection guide:
• User wants to change / improve the OUTLINE          → call update_outline
• User wants to change / improve the ARTICLE          → call update_article
• User asks for banner / ad / Facebook / LinkedIn copy → call update_banner_copy
• User asks for TikTok / video / Reels script          → call update_video_script
• User wants to set / change the article TITLE         → call set_title
• User wants to add a keyword                          → call add_keyword
• User just asks a question or gives feedback          → call reply

Always write real, complete content — never use placeholders or "[...]".
Output in Bahasa Indonesia unless the user explicitly asks for another language.`;
}

/* ─── Local fallback (no API required) ───────────────────────── */

/**
 * Pattern-match common instructions and apply simple store mutations
 * without any API call. This keeps the UX responsive even when the
 * network is unavailable.
 */
function fallbackDispatch(instruction: string): string {
  const lower = instruction.toLowerCase();
  const store = useContentStore.getState();

  /* ── title mutations ── */
  if (lower.includes("title") || lower.includes("judul")) {
    // Try to extract quoted text as the new title
    const quoted = instruction.match(/["「『](.*?)["」』]/)?.[1];
    if (quoted) {
      store.setSelectedTitle(quoted);
      return `✅ Title set to: "${quoted}".`;
    }
    return `Instruction noted: "${instruction}". Please select a title from the title cards in the Agent chat, or tell me the exact title text in quotes.`;
  }

  /* ── outline mutations ── */
  if (
    (lower.includes("outline") || lower.includes("struktur")) &&
    store.outline
  ) {
    if (lower.includes("shorten") || lower.includes("shorter") || lower.includes("pendek")) {
      const lines = store.outline.split("\n").filter(Boolean);
      store.updateOutline(lines.slice(0, Math.ceil(lines.length * 0.7)).join("\n"));
      return "Done! I trimmed the outline to about 70% of its original length. You can continue editing above.";
    }
    if (lower.includes("add") || lower.includes("tambah")) {
      store.updateOutline(
        store.outline + "\n\n## Tambahan\nSeksi baru yang ditambahkan sesuai instruksi Anda."
      );
      return "Added a new section to the outline. Review and customise it as needed.";
    }
    return `Instruction noted: "${instruction}". Applying to outline...`;
  }

  /* ── article mutations ── */
  if (
    (lower.includes("article") || lower.includes("artikel") || lower.includes("intro")) &&
    store.articleHtml
  ) {
    return `Instruction noted: "${instruction}". Please click Generate Article again with this instruction — the API will apply it to the full article.`;
  }

  /* ── social copy ── */
  if (lower.includes("video") || lower.includes("tiktok") || lower.includes("reels")) {
    return "I'll write the video script now — click **⚡️ Generate Snippets** in Step 3 and I'll apply your instructions.";
  }
  if (lower.includes("banner") || lower.includes("facebook") || lower.includes("linkedin")) {
    return "I'll write the banner copy for you — click **⚡️ Generate Snippets** in Step 3 and I'll apply your instructions.";
  }

  /* ── generic ── */
  return `Got your instruction: **"${instruction}"**. I'm ready — continue with the next step and I'll incorporate this guidance.`;
}
